import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sprout, X } from "lucide-react";
import api from "../../utils/api";

/**
 * PassiveFeedbackWidget - Passive Feedback Loop Widget
 * 
 * A fixed bottom-right widget that allows users to provide feedback
 * with sentiment selection and optional comments.
 * 
 * States:
 * 1. Closed: Pill-shaped button with sprout icon
 * 2. Open: Card with sentiment icons, textarea, and submit button
 * 3. Success: Thank you message with animation
 */
const PassiveFeedbackWidget = () => {
  const [state, setState] = useState("closed"); // "closed" | "open" | "success"
  const [sentiment, setSentiment] = useState(null); // "negative" | "neutral" | "positive" | null
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successTimeout, setSuccessTimeout] = useState(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeout) {
        clearTimeout(successTimeout);
      }
    };
  }, [successTimeout]);

  const handleOpen = () => {
    setState("open");
    setError(null);
  };

  const handleClose = () => {
    setState("closed");
    setSentiment(null);
    setMessage("");
    setError(null);
    if (successTimeout) {
      clearTimeout(successTimeout);
      setSuccessTimeout(null);
    }
  };

  const handleSentimentClick = (value) => {
    setSentiment(value === sentiment ? null : value);
  };

  const handleSubmit = async () => {
    // Validation: must have sentiment OR message
    if (!sentiment && !message.trim()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        sentiment: sentiment || null,
        message: message.trim() || "",
        page_url: window.location.href || "",
      };

      await api.post("/api/feedback/passive/", payload);

      // Success: transition to success state
      setState("success");
      setIsSubmitting(false);

      // Auto-close after 2 seconds
      const timeout = setTimeout(() => {
        handleClose();
      }, 2000);
      setSuccessTimeout(timeout);
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      setError("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = !sentiment && !message.trim();

  // Mobile responsive bottom offset
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const bottomOffset = isMobile ? "80px" : "24px";

  return (
    <div
      style={{
        position: "fixed",
        bottom: bottomOffset,
        right: "24px",
        zIndex: 10000,
      }}
    >
      {/* Trigger Pill (Closed State) */}
      <AnimatePresence mode="wait">
        {state === "closed" && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={handleOpen}
            className="feedback-trigger"
            aria-label="Open feedback"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 20px",
              backgroundColor: "#2E7D32",
              color: "#FFFFFF",
              border: "none",
              borderRadius: "9999px",
              cursor: "pointer",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              fontSize: "14px",
              fontWeight: 500,
              transition: "all 0.2s ease",
            }}
            whileHover={{ scale: 1.05, backgroundColor: "#1B5E20" }}
            whileTap={{ scale: 0.95 }}
          >
            <Sprout size={18} />
            <span>Feedback</span>
          </motion.button>
        )}

        {/* Open State: Popover Card + Close Button */}
        {state === "open" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            style={{ position: "relative" }}
          >
            {/* Popover Card */}
            <div
              role="dialog"
              aria-label="Feedback form"
              style={{
                width: "300px",
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                marginBottom: "8px",
              }}
            >
              {/* Header */}
              <div style={{ marginBottom: "16px" }}>
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#303030",
                    margin: 0,
                  }}
                >
                  Help BloomPal Grow
                </h3>
              </div>

              {/* Sentiment Section */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                  gap: "8px",
                }}
              >
                {[
                  { emoji: "🍂", value: "negative", label: "Negative" },
                  { emoji: "🌰", value: "neutral", label: "Neutral" },
                  { emoji: "🌻", value: "positive", label: "Positive" },
                ].map(({ emoji, value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleSentimentClick(value)}
                    aria-label={`Select ${label} sentiment`}
                    style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "50%",
                      border: sentiment === value ? "3px solid #2E7D32" : "1px solid #E5E7EB",
                      backgroundColor: sentiment === value ? "#E8F5E9" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "24px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      flex: 1,
                    }}
                    onMouseEnter={(e) => {
                      if (sentiment !== value) {
                        e.currentTarget.style.backgroundColor =
                          value === "negative"
                            ? "#FEE2E2"
                            : value === "neutral"
                            ? "#FEF3C7"
                            : "#D1FAE5";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (sentiment !== value) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>

              {/* Text Input Section */}
              <div style={{ marginBottom: "16px" }}>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What's on your mind? Spot a bug or have an idea?"
                  rows={3}
                  style={{
                    width: "100%",
                    minHeight: "80px",
                    padding: "10px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                  aria-label="Feedback message"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div
                  style={{
                    color: "#DC2626",
                    fontSize: "12px",
                    marginBottom: "12px",
                    textAlign: "center",
                  }}
                >
                  {error}
                </div>
              )}

              {/* Send Button */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitDisabled || isSubmitting}
                style={{
                  width: "100%",
                  padding: "12px",
                  backgroundColor: isSubmitDisabled ? "#9CA3AF" : "#2E7D32",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  opacity: isSubmitting ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitDisabled && !isSubmitting) {
                    e.currentTarget.style.backgroundColor = "#1B5E20";
                    e.currentTarget.style.transform = "scale(1.02)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitDisabled && !isSubmitting) {
                    e.currentTarget.style.backgroundColor = "#2E7D32";
                    e.currentTarget.style.transform = "scale(1)";
                  }
                }}
                aria-label="Send feedback"
              >
                {isSubmitting ? "Sending..." : "Send Feedback"}
              </button>
            </div>

            {/* Close Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={handleClose}
              aria-label="Close feedback"
              style={{
                position: "absolute",
                bottom: "0",
                right: "0",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "#2E7D32",
                color: "#FFFFFF",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                transition: "all 0.2s ease",
              }}
              whileHover={{ scale: 1.1, backgroundColor: "#1B5E20" }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={18} />
            </motion.button>
          </motion.div>
        )}

        {/* Success State */}
        {state === "success" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            style={{
              width: "300px",
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              padding: "32px 16px",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            {/* Bloom Animation */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.2, 1] }}
              transition={{
                duration: 0.6,
                ease: "easeOut",
              }}
              style={{ fontSize: "48px", marginBottom: "16px" }}
            >
              🌻
            </motion.div>

            {/* Success Message */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#2E7D32",
                margin: 0,
              }}
            >
              Thanks for sharing!
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PassiveFeedbackWidget;
